import { useEffect, useState } from "react";
import { Box, Stack, Typography, IconButton, Paper } from "@mui/material";
import { Layers, Pencil, Check, X } from "lucide-react";
import PipelineMultiSelector from "./PipelineMultiSelector";
import PipelineStageEditor from "./PipelineStageEditor";
import { useSettings } from "../../../context/SettingsContext";
import PageTitle from "../../../components/PageTitle";

export default function SettingsPipeline() {
  const { pipeline_status, savePipeline } = useSettings();

  const [editing, setEditing] = useState(false);
  const [stages, setStages] = useState([]);
  const [savedStages, setSavedStages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!pipeline_status?.length) return;
    setStages(pipeline_status);
    setSavedStages(pipeline_status);
  }, [pipeline_status]);

  const save = async () => {
    try {
      setSaving(true);
      await savePipeline(stages);
      setSavedStages(stages);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setStages(savedStages);
    setEditing(false);
  };

  return (
    <Box elevation={0}>
      <Stack direction="row" justifyContent="space-between" mb={4}>
        <PageTitle parts={["Settings", "Pipelines", "Status"]} />

        {!editing ? (
          <IconButton
            onClick={() => setEditing(true)}
            sx={{
              bgcolor: "#4F46E5",
              color: "white",
              borderRadius: 999,
              px: 2,
              py: 1,
              "&:hover": {
                bgcolor: "#4338CA",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(79,70,229,0.45)",
              },
            }}
          >
            <Pencil size={16} />
          </IconButton>
        ) : (
          <Stack direction="row" spacing={1}>
            <IconButton
              disabled={saving}
              onClick={save}
              sx={{
                bgcolor: "#16A34A",
                color: "white",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "#15803D",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(22,163,74,0.45)",
                },
              }}
            >
              <Check size={16} />
            </IconButton>

            <IconButton
              disabled={saving}
              onClick={cancel}
              sx={{
                bgcolor: "rgba(220,38,38,0.15)",
                color: "#DC2626",
                borderRadius: 3,
                px: 2,
                py: 1,
                "&:hover": {
                  bgcolor: "rgba(220,38,38,0.25)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(220,38,38,0.35)",
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        <PipelineMultiSelector
          title="Pipeline Stages"
          icon={Layers}
          selected={stages}
          setSelected={setStages}
          editing={editing}
        />

        <PipelineStageEditor
          flow={stages}
          setFlow={setStages}
          editing={editing}
        />
      </Box>
    </Box>
  );
}
